import React, { memo, useCallback, useMemo, useState } from "react";
import { Col, Form } from "react-bootstrap";
import { useFormContext, Controller } from "react-hook-form";

import { HookFormInputGroup, RequiredMark } from "components";
import { FormRow } from "components/layout";
import Select from "components/select";
import { feesExceptionsForAuroraAMS, pricingMethods, fees } from "models";
import { float4Normalizer } from "validators";
import {
	currencyFourSigns,
	maxValue100,
	maxValue999999999999999_9999ToFixed4,
	onlyPercent,
	required,
} from "validators/validation";

import styles from "./scheduleA.module.scss";

type FeesProps = {
	isDisabled: boolean;
	isLoading: boolean;
	index: number;
	fee: any;
	isBusinessUnitAuroraOrAMS: boolean;
};

export const Fees = memo(
	({
		index,
		fee,
		isDisabled,
		isLoading,
		isBusinessUnitAuroraOrAMS,
	}: FeesProps) => {
		const [requiredAmount, setRequiredAmount] = useState<boolean>(false);
		const { register, control, trigger, watch, formState, setValue } =
			useFormContext();

		const { errors } = formState;

		const { isDirty } = formState;

		const pricingMethod = watch<string>(`fees.${index}.pricing_method`);
		const areFeesNotApplicable = watch<string>("are_fees_not_applicable");

		const checkIsPricingMethodAbsolute = useCallback(
			value => value === pricingMethods.absolute.value,
			[],
		);

		const isPricingMethodAbsolute = useMemo(
			() => checkIsPricingMethodAbsolute(pricingMethod?.value),
			[checkIsPricingMethodAbsolute, pricingMethod],
		);

		const isRateFee = useMemo(() => {
			const allowedForRates = [
				fees.amexOptBlueFee,
				fees.binSponsorshipFee,
				fees.voyagerRateFee,
				fees.wexDiscountFee,
				fees.platformFee,
			];
			return allowedForRates.includes(fee.feeName);
		}, [fee.feeName]);

		const warningMessage = useMemo(() => {
			return isRateFee
				? "Only rate should be entered"
				: "Only per item fee should be entered";
		}, [isRateFee]);

		const defaultRevenueShare = useMemo(
			() =>
				isBusinessUnitAuroraOrAMS
					? feesExceptionsForAuroraAMS.find(
							feesException =>
								feesException.feeName === fee.feeName,
					  )?.revenue_share
					: fee.revenue_share,
			[fee.feeName, fee.revenue_share, isBusinessUnitAuroraOrAMS],
		);

		const pricingMethodChange = async pricingMethod => {
			await setRequiredAmount(isPricingMethodAbsolute);

			if (!checkIsPricingMethodAbsolute(pricingMethod?.value)) {
				setValue(`fees.${index}.rate`, "");
				setValue(`fees.${index}.per_item_fee`, "");
			}
			isDirty &&
				trigger([`fees.${index}.rate`, `fees.${index}.per_item_fee`]);
		};

		const requiredRate = useCallback(
			value => {
				return !isPricingMethodAbsolute ||
					requiredAmount ||
					!isRateFee ||
					areFeesNotApplicable
					? undefined
					: required(value);
			},
			[
				areFeesNotApplicable,
				isPricingMethodAbsolute,
				isRateFee,
				requiredAmount,
			],
		);

		const requiredPerItemFee = useCallback(
			value => {
				return !isPricingMethodAbsolute ||
					requiredAmount ||
					isRateFee ||
					areFeesNotApplicable
					? undefined
					: required(value);
			},
			[
				areFeesNotApplicable,
				isPricingMethodAbsolute,
				isRateFee,
				requiredAmount,
			],
		);

		const normalizeOnBlur = useCallback(
			value => (value === "" ? value : float4Normalizer(value)),
			[],
		);
		return (
			<FormRow className="mb-4">
				<Col className={styles.pricingText}>
					{fee.feeLabel}{" "}
					<RequiredMark className={styles.requiredMark} />
				</Col>
				<Col>
					<Controller
						control={control}
						name={`fees.${index}.pricing_method`}
						defaultValue={fee.pricing_method}
						render={({ field, ...props }) => (
							<Select
								{...field}
								{...props}
								options={Object.values(pricingMethods)}
								getOptionLabel={o => o.label}
								getOptionValue={o => o.value}
								placeholder="Please select"
								onChange={value => {
									field.onChange(value);
									pricingMethodChange(value);
								}}
								defaultValue={fee.pricing_method}
								loading={isLoading}
								isSearchable={false}
								isDisabled={
									isDisabled || fee.isPricingMethodDisabled
								}
							/>
						)}
					/>
					{isPricingMethodAbsolute && (
						<Form.Control.Feedback className="text-warning d-block">
							{warningMessage}
						</Form.Control.Feedback>
					)}
				</Col>
				<Col>
					<HookFormInputGroup
						colInput={12}
						name={`fees.${index}.rate`}
						register={register}
						errors={errors}
						disabled={
							!isPricingMethodAbsolute || !isRateFee || isDisabled
						}
						validator={{
							required: requiredRate,
							maxValue100,
						}}
						normalize={currencyFourSigns}
						normalizeOnBlur={normalizeOnBlur}
						defaultValue={fee.rate}
						blurLoading={isLoading}
					/>
				</Col>
				<Col>
					<HookFormInputGroup
						colInput={12}
						name={`fees.${index}.per_item_fee`}
						register={register}
						errors={errors}
						disabled={
							!isPricingMethodAbsolute || isRateFee || isDisabled
						}
						validator={{
							required: requiredPerItemFee,
							maxValue999999999999999_9999ToFixed4,
							...fee?.perItemFeeValidate,
						}}
						normalize={currencyFourSigns}
						normalizeOnBlur={normalizeOnBlur}
						defaultValue={fee.per_item_fee}
						blurLoading={isLoading}
					/>
				</Col>
				<Col>
					<HookFormInputGroup
						colInput={12}
						name={`fees.${index}.revenue_share`}
						register={register}
						errors={errors}
						validator={{
							maxValue100,
						}}
						normalize={onlyPercent}
						defaultValue={defaultRevenueShare}
						blurLoading={isLoading}
						disabled={isDisabled || fee.isRevenueShareDisabled}
					/>
				</Col>
				<Col className={styles.pricingText}>{fee.calculationLabel}</Col>
			</FormRow>
		);
	},
);
